import { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "../i18n";

type Facing = "user" | "environment";

/**
 * Live still capture via getUserMedia + canvas.
 * "Take photo" opens the camera preview; "Choose file" stays a separate path.
 */
export function LiveStillCapture({
  open,
  facing = "user",
  onCapture,
  onClose,
  title,
}: {
  open: boolean;
  facing?: Facing;
  onCapture: (file: File) => void;
  onClose: () => void;
  title?: string;
}) {
  const t = useT();
  const heading = title ?? t("capture.liveStill.titleDefault");
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
  }, []);

  useEffect(() => {
    if (!open) {
      stop();
      return;
    }

    let cancelled = false;
    setError(null);
    setReady(false);

    void (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(t("capture.liveStill.unavailable"));
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setReady(true);
      } catch {
        setError(t("capture.liveStill.permissionDenied"));
      }
    })();

    return () => {
      cancelled = true;
      stop();
    };
  }, [open, facing, stop, t]);

  const snap = () => {
    const video = videoRef.current;
    if (!video || !ready) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setError(t("capture.liveStill.frameFailed"));
      return;
    }
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError(t("capture.liveStill.saveFailed"));
          return;
        }
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
        stop();
        onCapture(file);
        onClose();
      },
      "image/jpeg",
      0.92
    );
  };

  if (!open) return null;

  return (
    <div className="live-still" role="dialog" aria-modal="true" aria-label={heading}>
      <div className="live-still__panel">
        <header className="live-still__head">
          <h2>{heading}</h2>
          <button type="button" className="live-still__close" onClick={onClose}>
            {t("capture.liveStill.close")}
          </button>
        </header>
        <div
          className={`live-still__frame${ready ? "" : " is-empty"}`}
          data-facing={facing}
        >
          <video ref={videoRef} className="live-still__video" muted playsInline autoPlay />
        </div>
        {error ? <p className="capture__error">{error}</p> : null}
        <div className="live-still__actions">
          <button
            type="button"
            className="capture__btn"
            disabled={!ready}
            onClick={snap}
          >
            {t("capture.liveStill.capture")}
          </button>
          <button type="button" className="capture__btn capture__btn--ghost" onClick={onClose}>
            {t("capture.liveStill.cancel")}
          </button>
        </div>
        <p className="live-still__hint">{t("capture.liveStill.hint")}</p>
      </div>
    </div>
  );
}
